import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography, Button } from "@mui/material";
import { useForm } from "react-hook-form";

import Input from "components/Input";
import EditMapModal from "components/CoverageAreaMap/components/EditMapModal";
import { Launch as LaunchIcon } from "@mui/icons-material";
import { useAppDispatch } from "store/hooks";
import { handleUpdateMapRequest } from "store/modules/Logistics/thunk.store";

import { useAuth } from "context/AuthContext";

import { drawMap } from "./helpers";

import * as Styled from "./styles";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";

interface CoverageAreaMapProps {
  selectedAreaData?: object;
  coverageAreaId: string;
  currentCoverageArea: any;
}

interface CoverageAreaData {
  mapLink: string;
}

const CoverageAreaMap = ({
  selectedAreaData,
  currentCoverageArea,
  coverageAreaId,
}: CoverageAreaMapProps) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useForm<CoverageAreaData>();

  const { dealerId } = useAuth();

  const dispatch = useAppDispatch();

  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const [isEditMapModalVisible, setIsEditMapModalVisible] = useState(false);
  const [shouldRenderMap, setShouldRenderMap] = useState(false);

  const handleSubmitMapUpdate = async (data: CoverageAreaData) => {
    await dispatch(
      handleUpdateMapRequest({ ...data, coverageAreaId, dealerId }),
    ).unwrap();
    setIsEditMapModalVisible(false);
  };

  const handleChangeShouldRenderMap = () => {
    setShouldRenderMap((state) => !state);
  };

  const handleChangeEditMapModalVisibility = () => {
    setIsEditMapModalVisible((state) => !state);
  };

  useEffect(() => {
    setShouldRenderMap(false);
  }, [coverageAreaId]);

  useEffect(() => {
    if (!shouldRenderMap || !selectedAreaData || !mapDivRef.current) return;

    const initializeMap = async () => {
      try {
        setOptions({ key: process.env.REACT_APP_GOOGLE_MAPS_KEY });
        await importLibrary("maps");

        mapRef.current = new google.maps.Map(
          mapDivRef.current as HTMLDivElement,
          {
            mapTypeControl: false,
            fullscreenControl: false,
            streetViewControlOptions: {
              position: google.maps.ControlPosition.LEFT_BOTTOM,
            },
            zoomControlOptions: {
              position: google.maps.ControlPosition.RIGHT_CENTER,
            },
          },
        );

        if (mapRef.current) {
          drawMap(mapRef.current, selectedAreaData || {}, true);
        }
      } catch (error) {
        console.error("Erro ao carregar Google Maps:", error);
      }
    };

    initializeMap();
  }, [shouldRenderMap, selectedAreaData]);

  return (
    <Styled.Container>
      <EditMapModal
        isOpen={isEditMapModalVisible}
        onClose={handleChangeEditMapModalVisibility}
        onSave={handleSubmitMapUpdate}
      />
      <Typography variant="caption">{t("coverageAreaMap")}</Typography>
      <Styled.MapContainer
        shouldRenderOverlay={!(shouldRenderMap && selectedAreaData)}
      >
        <Styled.MapContent>
          {shouldRenderMap && selectedAreaData ? (
            <Styled.Map ref={mapDivRef} />
          ) : (
            <Styled.MiddleActionsContainer>
              {selectedAreaData ? (
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleChangeShouldRenderMap}
                >
                  {t("loadMap")}
                </Button>
              ) : (
                <Styled.TypeLinkContainer>
                  <Typography display="block" mb={1.5} variant="caption">
                    {t("coverageAreaMap")}
                  </Typography>
                  <Input
                    name="mapLink"
                    error={errors.mapLink}
                    label={t("addCoverageArea")}
                    placeholder="https://www.google.com/maps/d/u/0/edit?mid=312312smnjbnneknNA32sE4&usp=sharing"
                    register={register}
                    fullWidth
                    registerOptions={{
                      required: t("requiredField"),
                      pattern: {
                        value:
                          /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/,
                        message: t("invalidUrl"),
                      },
                    }}
                  />
                </Styled.TypeLinkContainer>
              )}
            </Styled.MiddleActionsContainer>
          )}
        </Styled.MapContent>
      </Styled.MapContainer>
      <Styled.ButtonMap>
        <LaunchIcon fontSize="small" color="inherit" />
        <Styled.Link
          href={currentCoverageArea.map?.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          Link para o mapa: {currentCoverageArea.map?.link}
        </Styled.Link>
      </Styled.ButtonMap>
    </Styled.Container>
  );
};

export default CoverageAreaMap;
